import InputLabel from "@mui/material/InputLabel";
import Stack from "@mui/material/Stack";
import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { green, grey } from "@mui/material/colors";
import { inputTypes, objType } from "data/globalData";
import { LightTooltip } from "components/PimDashboard/CommonComponents/CustomToolTip/CustomToolTip";
import { useCallback } from "react";

const SelectInputField = ({
	data,
	setData,
	column,
	width,
	options,
	sx,
	isMultiple = false,
	onChange,
	error = false,
	disabled,
	required,
	disabledOptionFun,
	disableLabel = false,
	...rest
}) => {
	let { type, list, getOptionLabel } = options;

	if (type === objType) list = Object.keys(list)?.map((key) => list[key]);
	let optionsList = Array.isArray(list) ? [...list] : [];

	const multiple = isMultiple || column.input_type === inputTypes.MULTIPLE_SELECT.key;
	const selectedValue = data?.[column.key] ?? (multiple ? [] : null);

	// Normalize options
	if (typeof getOptionLabel === "function") {
		optionsList = list?.map((row) => {
			const { name, key } = getOptionLabel(row); // Get the name and key
			return {
				name, // { name } from getOptionLabel
				key, // { key } from getOptionLabel
				label: name,
			};
		});
	} else {
		optionsList = optionsList.map((row) => ({
			...row,
			label: row?.name ?? row?.label,
		}));
	}

	const allOptionKeys = optionsList.map((o) => o.key); // Get all the keys

	// Select All item
	const SELECT_ALL = { key: "__select_all__", label: "Select All" };
	const withSelectAll = multiple ? [SELECT_ALL, ...optionsList] : optionsList;

	const isAllSelected = multiple && selectedValue?.length === optionsList.length;
	const hasValues = Array.isArray(data[column.key]) && data[column.key].length > 0;

	let sxData = {
		// "& .MuiAutocomplete-inputRoot": {
		// 	display: "flex",
		// 	alignItems: "center",
		// 	flexWrap: "nowrap",
		// 	height: "40px",
		// 	overflow: "hidden",
		// 	width: "100%", // Ensure input takes the remaining width
		// 	borderRadius: "0px",
		// },
		// "& .MuiAutocomplete-tag": {
		// 	flex: hasValues ? "1 1 100%" : "0 0 0%", // Tag should take 60% width when there are selected values
		// 	width: hasValues ? "100%" : "0%",
		// 	overflow: "hidden",
		// 	whiteSpace: "nowrap",
		// 	textOverflow: "ellipsis",
		// 	marginRight: "4px",
		// 	transition: "all 0.3s ease",
		// 	display: hasValues ? "flex" : "none", // Only show tags when there are values
		// },
		// "& .MuiAutocomplete-input": {
		// 	flex: hasValues ? "0 0 0%" : "1 1 100%", // When tags are present, hide input; else take full width
		// 	minWidth: 0,
		// 	width: hasValues ? "1px" : "100%",
		// 	pointerEvents: hasValues ? "none" : "auto",
		// 	transition: "all 0.3s ease",
		// 	display: hasValues ? "none" : "block", // Only show tags when there are values
		// 	padding: 0,
		// },
		// // "& .MuiAutocomplete-clearIndicator": {
		// // 	display: "none",
		// // },
		// // On hover or focus — reveal input and resize tags
		// "&.Mui-focused .MuiAutocomplete-input, &:hover .MuiAutocomplete-input": hasValues
		// 	? {
		// 			flex: "0 0 40%",
		// 			width: "auto",
		// 			pointerEvents: "auto",
		// 			display: "block",
		// 		}
		// 	: {},
		// "&.Mui-focused .MuiAutocomplete-tag, &:hover .MuiAutocomplete-tag": hasValues
		// 	? {
		// 			flex: "0 0 60%",
		// 			maxWidth: "60%",
		// 			display: "flex",
		// 		}
		// 	: {},
	};
	if (sx) sxData = { ...sxData, ...sx };

	// Change handler
	const handleChange = (event, value) => {
		// If not multiple, handle normal single-select case
		if (!multiple) {
			const selected = value?.key ?? null;
			if (typeof onChange === "function") {
				onChange({ target: { value: selected } });
			} else {
				setData({ ...data, [column.key]: selected });
			}
			return;
		}

		// Handle multiple select case
		const hasSelectAll = value[value.length - 1]?.key === SELECT_ALL.key;
		const selectedKeys = value
			.filter((v) => v.key !== SELECT_ALL.key) // Exclude the "Select All"
			.map((v) => v.key); // Map selected values to their keys

		const finalKeys = hasSelectAll
			? isAllSelected
				? []
				: allOptionKeys // Select all or deselect all
			: selectedKeys; // Regular selection (no "Select All")

		// Update the state
		if (typeof onChange === "function") {
			onChange({ target: { value: finalKeys } });
		} else {
			setData({ ...data, [column.key]: finalKeys });
		}
	};

	const memoizedDisabledFun = useCallback(
		(column, data, option) => {
			return typeof disabledOptionFun === "function" ? disabledOptionFun(column, data, option) : false;
		},
		[data],
	);

	return (
		<Stack sx={{ width }}>
			{!disableLabel && (
				<LightTooltip title={column?.name + (required ? "*" : "")} placement="bottom-start" arrow>
					<InputLabel
						sx={{
							p: 1,
							overflow: "hidden",
							// color: grey[100],
						}}>
						{column?.name} {required && <span style={{ color: "red" }}>*</span>}
					</InputLabel>
				</LightTooltip>
			)}

			<Autocomplete
				disableCloseOnSelect={multiple}
				disabled={disabled}
				fullWidth
				size="small"
				multiple={multiple}
				options={withSelectAll}
				getOptionLabel={(option) => option.label}
				getOptionDisabled={(option) => option.key !== SELECT_ALL.key && memoizedDisabledFun(column, data, option)}
				value={
					multiple
						? optionsList.filter((opt) => selectedValue?.includes(opt.key))
						: (optionsList.find((opt) => opt.key === selectedValue) ?? null)
				}
				isOptionEqualToValue={(opt, val) => opt.key === val.key}
				onChange={handleChange}
				renderTags={(value) => (
					<span
						style={{
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
						}}>
						{value.map((opt) => opt.name).join(", ")}
					</span>
				)}
				// popupIcon={<></>} //  remove dropdown icon for tighter layout

				renderOption={(props, option, { selected }) => {
					const isSelectAll = option.key === SELECT_ALL.key;
					const isChecked = isSelectAll ? isAllSelected : selected;
					const isIndeterminate = isSelectAll && selectedValue.length > 0 && selectedValue.length < optionsList.length;

					return (
						<li {...props}>
							{multiple && (
								<Checkbox
									checked={isChecked}
									indeterminate={isIndeterminate}
									sx={{ margin: 0, padding: "2px", mr: 1 }}
									disableRipple
								/>
							)}
							<Typography color={isSelectAll ? green[600] : "inherit"}>{option.label}</Typography>
						</li>
					);
				}}
				renderInput={(params) => (
					<TextField
						{...params}
						placeholder={`Search ${column?.name}`}
						variant="outlined"
						error={!!error}
						helperText={typeof error === "string" ? error : ""}
					/>
				)}
				{...rest}
				sx={sxData}
			/>
		</Stack>
	);
};

export default SelectInputField;
